import DOMQuillEditor from "@/src/components/text-editor/custom-editor";
import { useTheme } from "@/src/styles/theme-provider";
import { useRef } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import { Text, View } from "react-native";

const themeColors = {
  light: {
    slateDefault: "#526581",
    onyxDefault: "#22272a",
    chalkDefault: "#FFFFFF",
    obsidianDefault: "#F2F4F6",
    ash3: "#DFE5EC",
  },
  dark: {
    slateDefault: "#8aaac8",
    onyxDefault: "#D7DADC",
    chalkDefault: "#141a1f",
    obsidianDefault: "#0f1113",
    ash3: "#2d3540",
  },
} as const;

export default function ContentRichField<T extends FieldValues>({
  label,
  name,
  control,
}: {
  label: string;
  name: Path<T>;
  control: Control<T>;
}) {
  const { field } = useController({ name, control });
  const { primaryPalette, resolvedScheme } = useTheme();
  const colors = themeColors[resolvedScheme];

  // Capture the initial value once — never changes after mount
  // This prevents the editor from remounting every time the user types
  const initialContentRef = useRef<string>(field.value ?? "");
  const editorKey = initialContentRef.current ? "has-content" : "empty";

  return (
    <View className="mb-4">
      <Text className="font-medium mb-2 text-onyx">{label}</Text>
      <View style={{ height: 320 }}>
        <DOMQuillEditor
          key={editorKey}
          dom={{ style: { flex: 1 } }}
          editorHeight={320}
          content={initialContentRef.current}
          onContentChange={(html) => {
            field.onChange(html);
            field.onBlur();
          }}
          primaryColor={primaryPalette.default}
          textColor={colors.onyxDefault}
          borderColor={colors.ash3}
          containerBg={colors.obsidianDefault}
          placeHolder={colors.slateDefault}
        />
      </View>
    </View>
  );
}
